'use strict';
/* ==========================================================================
   core-local/chance.js — "Tails Is Due!": the probability model (DESIGN.md §4).
   Pure: no DOM, no engine globals. Runs in node (module.exports: tools/chancecheck.js, tools/contentcheck.js) and in
   the page (window.Chance). Registers itself with the question bank as `chance` when core/bank.js is loaded first
   (lesson.json scripts order), so a pool expression may say chance.ftext(a / (a + b)).

   EXACT ARITHMETIC: every probability is a reduced fraction {n, d} of integers (d > 0). A decimal (0.85) becomes a
   fraction through its decimal string (17/20), never through float division; a content formula ("g / (g + s)") is
   evaluated EXACTLY by a small + − × ÷ ( ) evaluator over the params (anything else falls back to the shared bank's
   evaluator and the result is turned back into a small-denominator fraction). Grade-7 scope (facts Correction 1): single
   experiments only, every listed outcome equally likely (a spinner = EQUAL slices, labels may repeat; a bag = one
   outcome per item, drawn with replacement).

   ---- fractions ----
   Chance.frac(v)              → {n, d}  from an int, a number (exact via its decimal string), "3/4", {n, d}
   Chance.rational(x, maxD)    → {n, d}  a float → the exact small-denominator fraction it is (d ≤ maxD = 10000)
   Chance.eq / add / sub / mul / div / toNumber / isInt / cmp
   Chance.text(q)              → "3/8", "1", "0", "−1/2"   (reduced)
   Chance.dec(q)               → "0.375" (terminating) or "about 0.33"
   Chance.pct(q)               → "37.5%" (terminating) or "about 33.33%"
   Chance.decpct(q)            → "0.75 = 75%"
   Chance.likelihood(q)        → 'impossible' | 'unlikely' | 'equally likely' | 'likely' | 'certain'
   ---- experiments (data; the notebook pack draws them in ink) ----
   Chance.exp(def, params)     → {kind, outcomes: [{key, label, value, group}], groups: [{label, count}], labels, n}
                                 def.kind: coin | coins (n: 1 only, grade 7) | die | spinner (slices: [[label, k]] or
                                 [{label, slices}]) | bag (items: [[label, k]] or [{label, count}]) | tiles (word) |
                                 cards (from, to) | streak (coin: true | die: true: the next flip/roll after a streak).
                                 k may be a number or a param name / expression.
                                 Also Chance.coin(), die(), spinner(slices), bag(items), tiles(word), cards(from, to).
   Chance.space(exp)           → the equally likely outcomes, in order (a spinner's slices clockwise as listed)
   Chance.event(exp, name)     → predicate(outcome). Names: is:X (key or label), label:X, not:X, even, odd, vowel,
                                 multiple:k, any. (Event text may hold {param}: filled by Chance.fillEvent.)
   Chance.count(exp, name)     → the number of favourable outcomes
   Chance.P(exp, name)         → exact P = favourable ÷ total
   Chance.expected(exp, name, n) → n · P (exact)
   Chance.value(exp, name)     → what a poster claim about `name` must show: P, or for "total"/"size" the number of
                                 outcomes, "count:EV" the favourable count, "expect:EV=n" the expected count
                                 (EV without ':' means is:EV)
   Chance.working(exp, name)   → {n, f, lines: ["Outcomes: 1, 2, 3, 4, 5, 6 (6)", "Favorable (even): 2, 4, 6 (3)",
                                  "P = 3 ÷ 6 = 1/2"], space: [labels], fav: [bool]}  (what a tap on a claim shows)
   ---- simulation (seeded, deterministic) ----
   Chance.rng(seed)            → mulberry32 (seed: an unsigned int; a string is hashed first)
   Chance.simulate(exp, n, seed, event?) → {seed, n, idx: [outcome index], outcomes: [key], tally: {key: count}}
                                 + with an event: x (hits), running: [hits after trial 1..n]
   Chance.VIDEO                → the video's fixed experiments: wheel (s3/s4), s4Seed (8 spins: FREE SOCKS, TRY AGAIN,
                                 10% OFF, FREE SOCKS, then 4 without socks), s4Big {seed, n: 800, socks}
   ---- Fine Print (content/bank.print.json) ----
   Chance.CHEATS               → ['equiprobable', 'partToPart', 'gamblersFallacy', 'smallSample', 'complement',
                                  'conversion']   (+ 'legit' = no cheat)
   Chance.cheatValue(name, exp, event) → the cheat's number where it has one (equiprobable 1/labels, partToPart
                                 f/(n − f), complement 1 − P, conversion the digit slip 0.<n><d>), else null
   Chance.isCheat(name, shown, exp, event, params, rawText) → does `shown` match the cheat's definition (bank.print notes)
   Chance.shown(text, event)   → {value, values, chainOk}: the number a claim's text shows (the chain after its last
                                 "P(…)"; "total": the sum of its whole numbers; "expect": its last number)
   Chance.poster(item, params) → {id, title, cheat, level, exp, bad, claims: [{text, event, shown, honest, ok, working}],
                                 q, answer, answerText, distractors: {name: {value, text}}, explain}
   ---- the Lab (content/bank.lab.json) ----
   Chance.steps(item, params, seed) → [{i, type, text, hint, format, last, value, answerText | options, correct, truth |
                                 trials, seed, sim}]  (a run step simulates the item's exp with a seed derived from
                                 `seed`, sets params x = hits and N = trials for the steps after it)
   Chance.check(step, typed)   → numpad: exact compare (frac: "3/4" = "6/8" = "0.75" = "75%"; int: whole numbers;
                                 num: the decimal, or the percent with or without "%"); choice: index; run: true
   Chance.parseAnswer(str)     → {n, d} | null   ("3/4", "6/8", "0.75", ".75", "75%", "5,000", "−1/2")
   ---- the bank side (the SHARED evaluator: core/bank.js; nothing re-implemented) ----
   Chance.exact(expr, params)  → {n, d}
   Chance.format(v, fmt)       → frac | decpct | int | num | pct | dec | (else Bank.format)
   Chance.ftext / dptext / ntext (v) → format(v, 'frac' | 'decpct' | 'num') (the bank calls these from rewritten text)
   Chance.fill(text, params)   → Bank.fill, also accepting {expr|frac}, {expr|decpct}, {expr|num}
   Chance.fillEvent(name, params)
   Chance.pool(json)           → a copy core/bank.js accepts (see pool() below); the games pass THIS to Bank.attempt
   Chance.source(item)         → the content item behind a pooled copy (item._src || item)
   Chance.byLevel(instances)   → the instances sorted easiest first (item.level, stable)
   ========================================================================== */
(function (root) {
  /* ---------------- exact fractions ---------------- */
  const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
  function mk(n, d) {
    if (!Number.isSafeInteger(n) || !Number.isSafeInteger(d) || d === 0) throw new Error(`chance: bad fraction ${n}/${d}`);
    if (d < 0) { n = -n; d = -d; }
    const k = gcd(n, d);
    return { n: n / k, d: d / k };
  }
  function frac(v) {
    if (v && typeof v === 'object' && 'n' in v && 'd' in v) return mk(v.n, v.d);
    if (typeof v === 'string') { const p = parseAnswer(v); if (!p) throw new Error(`chance: "${v}" is not a number`); return p; }
    if (typeof v !== 'number' || !isFinite(v)) throw new Error(`chance: ${v} is not a finite number`);
    if (Number.isInteger(v)) return mk(v, 1);
    const s = String(v);
    if (/e/i.test(s)) return rational(v);
    const neg = s[0] === '-', [ip, fp = ''] = s.replace('-', '').split('.');
    if (fp.length > 9) return rational(v);                 // 0.1 + 0.2 style float noise: the small fraction it is
    const d = Math.pow(10, fp.length), n = +ip * d + +fp;
    return mk(neg ? -n : n, d);
  }
  /* a float → the exact fraction with the smallest denominator ≤ maxD within 1e-9 (continued fractions) */
  function rational(x, maxD = 10000) {
    if (!isFinite(x)) throw new Error(`chance: ${x} is not finite`);
    if (Number.isInteger(x)) return mk(x, 1);
    const neg = x < 0; let y = Math.abs(x);
    let h0 = 0, h1 = 1, k0 = 1, k1 = 0;
    for (let i = 0; i < 40; i++) {
      const a = Math.floor(y), h2 = a * h1 + h0, k2 = a * k1 + k0;
      if (k2 > maxD) break;
      h0 = h1; h1 = h2; k0 = k1; k1 = k2;
      if (Math.abs(h1 / k1 - Math.abs(x)) < 1e-9) break;
      const r = y - a; if (r < 1e-12) break; y = 1 / r;
    }
    if (!k1 || Math.abs(h1 / k1 - Math.abs(x)) > 1e-9) throw new Error(`chance: ${x} is not a small-denominator fraction`);
    return mk(neg ? -h1 : h1, k1);
  }
  const F = frac;
  const add = (a, b) => { a = F(a); b = F(b); return mk(a.n * b.d + b.n * a.d, a.d * b.d); };
  const sub = (a, b) => { a = F(a); b = F(b); return mk(a.n * b.d - b.n * a.d, a.d * b.d); };
  const mul = (a, b) => { a = F(a); b = F(b); return mk(a.n * b.n, a.d * b.d); };
  const div = (a, b) => { a = F(a); b = F(b); if (b.n === 0) throw new Error('chance: ÷ 0'); return mk(a.n * b.d, a.d * b.n); };
  const eq = (a, b) => { if (a == null || b == null) return a == null && b == null; a = F(a); b = F(b); return a.n === b.n && a.d === b.d; };
  const cmp = (a, b) => { const s = sub(a, b); return Math.sign(s.n); };
  const toNumber = q => { q = F(q); return q.n / q.d; };
  const isInt = q => F(q).d === 1;
  const ZERO = mk(0, 1), ONE = mk(1, 1);
  function terminates(q) { let d = F(q).d; while (d % 2 === 0) d /= 2; while (d % 5 === 0) d /= 5; return d === 1; }
  const minus = s => s.replace(/^-/, '−');
  const commas = s => s.replace(/\B(?=(\d{3})+$)/g, ',');
  function roundTo(x, d = 0) { x = +x; if (!isFinite(x)) return x; const f = Math.pow(10, d), u = Number((Math.abs(x) * f).toPrecision(12)); return Math.sign(x) * Math.round(u) / f; }
  function text(q) { q = F(q); return minus(q.d === 1 ? String(q.n) : `${q.n}/${q.d}`); }
  function dec(q) {
    q = F(q);
    if (!terminates(q)) return 'about ' + minus(String(roundTo(q.n / q.d, 2)));
    const neg = q.n < 0, n = Math.abs(q.n), ip = Math.floor(n / q.d);
    let r = n % q.d, fp = '';
    for (let i = 0; r && i < 12; i++) { r *= 10; fp += Math.floor(r / q.d); r %= q.d; }
    return (neg ? '−' : '') + commas(String(ip)) + (fp ? '.' + fp : '');
  }
  function pct(q) { return dec(mul(q, 100)) + '%'; }
  const decpct = q => `${dec(q)} = ${pct(q)}`;
  function likelihood(q) {
    const c0 = cmp(q, ZERO), c1 = cmp(q, ONE), ch = cmp(q, mk(1, 2));
    if (c0 < 0 || c1 > 0) return 'not a probability';
    return c0 === 0 ? 'impossible' : c1 === 0 ? 'certain' : ch === 0 ? 'equally likely' : ch < 0 ? 'unlikely' : 'likely';
  }

  /* typed / printed answers: "3/4", "6/8", "0.75", ".75", "75%", "5,000", "−1/2", "1 / 2" → exact {n, d} (null: not one) */
  function parseAnswer(str) {
    if (str && typeof str === 'object' && 'n' in str) return mk(str.n, str.d);
    if (typeof str === 'number') return isFinite(str) ? frac(str) : null;
    const s = String(str == null ? '' : str).replace(/[\s,]/g, '').replace(/[−–]/g, '-');
    let m;
    if ((m = /^(-?)(\d+)\/(\d+)$/.exec(s))) return +m[3] === 0 ? null : mk((m[1] ? -1 : 1) * +m[2], +m[3]);
    if ((m = /^(-?)(\d+\.?\d*|\.\d+)(%?)$/.exec(s))) {
      const [ip, fp = ''] = m[2].split('.'), d = Math.pow(10, fp.length);
      const q = mk((m[1] ? -1 : 1) * ((+ip || 0) * d + (+fp || 0)), d);
      return m[3] ? div(q, 100) : q;
    }
    return null;
  }

  /* ---------------- experiments ---------------- */
  const LETTERS_V = new Set(['A', 'E', 'I', 'O', 'U']);
  function countOf(v, params) {
    if (typeof v === 'number') return v;
    const k = String(v).trim();
    if (params && Object.prototype.hasOwnProperty.call(params, k)) return +params[k];
    if (/^\d+$/.test(k)) return +k;
    const q = exact(k, params || {});
    if (!isInt(q)) throw new Error(`chance: count "${v}" is not a whole number`);
    return q.n;
  }
  const pairs = (list, key, params) => (list || []).map(e => (Array.isArray(e) ? { label: String(e[0]), count: countOf(e[1], params) }
    : { label: String(e.label), count: countOf(e[key] != null ? e[key] : e.count != null ? e.count : e.slices, params) }));
  function build(kind, outcomes, groups, extra = {}) {
    outcomes.forEach((o, i) => { o.i = i; });
    const labels = [...new Set(outcomes.map(o => o.label))];
    return { kind, outcomes, groups: groups || labels.map(l => ({ label: l, count: outcomes.filter(o => o.label === l).length })), labels, n: outcomes.length, ...extra };
  }
  function grouped(kind, groups) {
    const outcomes = [];
    groups.forEach((gr, gi) => { if (!(Number.isInteger(gr.count) && gr.count >= 0)) throw new Error(`chance: ${kind} ${gr.label}: ${gr.count} is not a count`); for (let k = 0; k < gr.count; k++) outcomes.push({ key: gr.label, label: gr.label, value: gr.label, group: gi }); });
    if (!outcomes.length) throw new Error(`chance: an empty ${kind}`);
    return build(kind, outcomes, groups.map(gr => ({ label: gr.label, count: gr.count })));
  }
  const coin = () => build('coin', [{ key: 'H', label: 'H', value: 'H', group: 0 }, { key: 'T', label: 'T', value: 'T', group: 1 }]);
  const die = () => build('die', [1, 2, 3, 4, 5, 6].map((v, i) => ({ key: String(v), label: String(v), value: v, group: i })));
  const spinner = (slices, params) => grouped('spinner', pairs(slices, 'slices', params));
  const bag = (items, params) => grouped('bag', pairs(items, 'count', params));
  const tiles = word => build('tiles', [...String(word).toUpperCase()].map((c, i) => ({ key: c, label: c, value: c, group: i })), null, { word: String(word).toUpperCase() });
  function cards(from, to) {
    const out = [];
    for (let v = from; v <= to; v++) out.push({ key: String(v), label: String(v), value: v, group: v - from });
    if (!out.length) throw new Error('chance: no cards');
    return build('cards', out, null, { from, to });
  }
  function exp(def, params = {}) {
    if (def && def.outcomes && def.kind) return def;                     // already built
    if (!def || typeof def !== 'object') throw new Error('chance: an experiment is {kind, ...}');
    switch (def.kind) {
      case 'coin': return coin();
      case 'coins': { const k = def.n == null ? 1 : countOf(def.n, params); if (k !== 1) throw new Error('chance: coins(k > 1) is grade 8 (facts Correction 1)'); return coin(); }
      case 'die': return die();
      case 'spinner': return spinner(def.slices, params);
      case 'bag': return bag(def.items, params);
      case 'tiles': return tiles(def.word);
      case 'cards': return cards(countOf(def.from, params), countOf(def.to, params));
      case 'streak': { const e = def.die ? die() : coin(); e.streak = true; return e; }
      default: throw new Error(`chance: unknown experiment kind "${def.kind}"`);
    }
  }
  const space = e => exp(e).outcomes;

  /* events: a small whitelist of predicate names */
  function event(e, name) {
    const s = String(name);
    const m = /^([a-z]+)(?::(.*))?$/i.exec(s);
    if (!m) throw new Error(`chance: bad event "${name}"`);
    const [, op, arg] = m;
    switch (op) {
      case 'is': return o => o.key === arg || o.label === arg;
      case 'label': return o => o.label === arg;
      case 'not': return o => !(o.key === arg || o.label === arg);
      case 'even': return o => typeof o.value === 'number' && o.value % 2 === 0;
      case 'odd': return o => typeof o.value === 'number' && Math.abs(o.value % 2) === 1;
      case 'vowel': return o => LETTERS_V.has(String(o.value));
      case 'multiple': { const k = +arg; if (!(k > 0)) throw new Error(`chance: multiple:${arg}`); return o => typeof o.value === 'number' && o.value % k === 0; }
      case 'any': return () => true;
      default: throw new Error(`chance: "${name}" is not a probability event`);
    }
  }
  function count(e, name) { e = exp(e); const f = event(e, name); return e.outcomes.filter(f).length; }
  function P(e, name) { e = exp(e); return mk(count(e, name), e.n); }
  const expected = (e, name, n) => mul(P(e, name), n);
  /* the value a poster claim about `name` must show */
  function value(e, name) {
    e = exp(e);
    const s = String(name);
    if (s === 'total' || s === 'size') return mk(e.n, 1);
    let m;
    if ((m = /^count:(.+)$/.exec(s))) return mk(count(e, m[1]), 1);
    if ((m = /^expect:(.+)=(\d+)$/.exec(s))) { const ev = m[1].includes(':') ? m[1] : 'is:' + m[1]; return expected(e, ev, +m[2]); }
    return P(e, s);
  }
  const isProbEvent = name => !/^(total|size|count:|expect:)/.test(String(name));
  /* what a tap on a claim shows (the sample space listed, the favourable ones marked) */
  const NOUN = { coin: 'Outcomes', die: 'Outcomes', spinner: 'Equal slices', bag: 'In the bag', tiles: 'Tiles', cards: 'Cards' };
  function evName(name) { const s = String(name); const m = /^(\w+):(.*)$/.exec(s); if (!m) return s; return m[1] === 'not' ? 'not ' + m[2] : m[1] === 'multiple' ? 'multiples of ' + m[2] : m[2]; }
  function listing(e, fav) {
    if (e.kind === 'spinner' || e.kind === 'bag') return e.groups.map(gr => `${gr.label} ${gr.count}`).join(', ');
    return e.outcomes.map(o => o.label).join(', ');
  }
  function working(e, name) {
    e = exp(e);
    const s = String(name), noun = NOUN[e.kind] || 'Outcomes', lines = [];
    const all = `${noun}: ${listing(e)} (${e.n}${e.kind === 'spinner' || e.kind === 'bag' ? ' in all' : ''})`;
    lines.push(all);
    if (s === 'total' || s === 'size') {
      if (e.kind === 'spinner' || e.kind === 'bag') lines.push(`${e.groups.map(gr => gr.count).join(' + ')} = ${e.n}`);
      return { n: e.n, f: null, lines, space: e.outcomes.map(o => o.label), fav: e.outcomes.map(() => true) };
    }
    let m;
    if ((m = /^expect:(.+)=(\d+)$/.exec(s))) {
      const ev = m[1].includes(':') ? m[1] : 'is:' + m[1], p = P(e, ev), v = expected(e, ev, +m[2]);
      lines.push(`P(${evName(ev)}) = ${text(p)}`, `Expected: ${commas(m[2])} × ${text(p)} = ${isInt(v) ? commas(String(v.n)) : text(v)}`);
      return { n: e.n, f: count(e, ev), lines, space: e.outcomes.map(o => o.label), fav: e.outcomes.map(event(e, ev)) };
    }
    const pred = event(e, s), fav = e.outcomes.map(pred), f = fav.filter(Boolean).length, p = mk(f, e.n);
    const favLabels = e.kind === 'spinner' || e.kind === 'bag' ? String(f) : (f ? `${e.outcomes.filter(pred).map(o => o.label).join(', ')} (${f})` : 'none (0)');
    lines.push(`Favorable (${evName(s)}): ${favLabels}`);
    lines.push(`P = ${f} ÷ ${e.n} = ${text(p)}`);
    return { n: e.n, f, lines, space: e.outcomes.map(o => o.label), fav };
  }

  /* ---------------- seeded simulation ---------------- */
  function hashSeed(s) {
    s = String(s); let h = 1779033703 ^ s.length;
    for (let i = 0; i < s.length; i++) { h = Math.imul(h ^ s.charCodeAt(i), 3432918353); h = (h << 13) | (h >>> 19); }
    h = Math.imul(h ^ (h >>> 16), 2246822507); h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^ (h >>> 16)) >>> 0;
  }
  function rng(seed) {
    let a = typeof seed === 'number' && Number.isInteger(seed) ? seed >>> 0 : hashSeed(seed);
    const r = () => { a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    r.int = n => Math.floor(r() * n);
    return r;
  }
  function simulate(e, n, seed, ev) {
    e = exp(e);
    if (!(Number.isInteger(n) && n >= 0 && n <= 1e6)) throw new Error(`chance.simulate: n = ${n}`);
    const r = rng(seed), idx = new Array(n), outcomes = new Array(n), tally = {};
    e.outcomes.forEach(o => { tally[o.key] = 0; });
    for (let i = 0; i < n; i++) { const k = r.int(e.n); idx[i] = k; outcomes[i] = e.outcomes[k].key; tally[outcomes[i]]++; }
    const out = { seed, n, idx, outcomes, tally };
    if (ev != null) {
      const f = event(e, ev), running = new Array(n); let x = 0;
      for (let i = 0; i < n; i++) { if (f(e.outcomes[idx[i]])) x++; running[i] = x; }
      out.event = String(ev); out.x = x; out.running = running;
    }
    return out;
  }

  /* the video's experiments (DESIGN.md §5.1 s3/s4; tools/chancecheck.js proves both seeds) */
  const WHEEL = { kind: 'spinner', slices: [['FREE SOCKS', 1], ['10% OFF', 3], ['TRY AGAIN', 4]] };
  const VIDEO = {
    wheel: WHEEL,
    gumballs: { kind: 'bag', items: [['RED', 3], ['BLUE', 1]] },
    s4Seed: 153,                                 // 8 spins: FREE SOCKS, TRY AGAIN, 10% OFF, FREE SOCKS, then no socks
    s4Order: ['FREE SOCKS', 'TRY AGAIN', '10% OFF', 'FREE SOCKS'],
    s4Big: { seed: 0, n: 800, socks: 0 },        // filled below from the model (never typed in)
  };

  /* ---------------- the bank side ---------------- */
  function BANK() {
    let B = root.Bank;
    if (!B && typeof require === 'function') B = require('../../../shared/engine/v2/core/bank.js');
    if (!B) throw new Error('chance: core/bank.js is not loaded');
    if (!B.registered().includes('chance')) B.register('chance', Chance);
    return B;
  }
  /* exact + − × ÷ ( ) over numbers and params; anything else → the shared evaluator, then back to a fraction */
  function exact(expr, params = {}) {
    const s = String(expr), toks = [];
    const re = /\s*(?:(\d+\.?\d*|\.\d+)|([A-Za-z_$][\w$]*)|(.))/gy;
    let m, simple = true;
    while (re.lastIndex < s.length && (m = re.exec(s))) {
      if (m[1] != null) toks.push({ t: 'n', v: m[1] });
      else if (m[2] != null) { if (!Object.prototype.hasOwnProperty.call(params, m[2]) || typeof params[m[2]] !== 'number') { simple = false; break; } toks.push({ t: 'n', v: params[m[2]] }); }
      else if (m[3] != null && /[-+*/()]/.test(m[3])) toks.push({ t: m[3] });
      else if (m[3] != null && /\s/.test(m[3])) continue;
      else { simple = false; break; }
    }
    if (simple) {
      let i = 0;
      const peek = () => toks[i] && toks[i].t, take = () => toks[i++];
      const prim = () => {
        const k = take();
        if (!k) throw new Error(`chance: "${s}" ends early`);
        if (k.t === 'n') return frac(k.v);
        if (k.t === '-') return mul(prim(), -1);
        if (k.t === '+') return prim();
        if (k.t === '(') { const v = sum(); if (take()?.t !== ')') throw new Error(`chance: "${s}": missing )`); return v; }
        throw new Error(`chance: "${s}": unexpected ${k.t}`);
      };
      const prod = () => { let v = prim(); while (peek() === '*' || peek() === '/') { const o = take().t, w = prim(); v = o === '*' ? mul(v, w) : div(v, w); } return v; };
      const sum = () => { let v = prod(); while (peek() === '+' || peek() === '-') { const o = take().t, w = prod(); v = o === '+' ? add(v, w) : sub(v, w); } return v; };
      const v = sum();
      if (i !== toks.length) throw new Error(`chance: "${s}": unexpected ${toks[i].t}`);
      return v;
    }
    const v = BANK().eval(s, params);
    if (typeof v === 'string') { const q = parseAnswer(v); if (!q) throw new Error(`chance: "${s}" gives "${v}"`); return q; }
    return frac(v);
  }
  function format(v, fmt, unit) {
    const q = () => (typeof v === 'number' ? (Number.isInteger(v) ? mk(v, 1) : frac(v)) : F(v));
    switch (fmt) {
      case 'frac': return text(q());
      case 'decpct': return decpct(q());
      case 'num': case 'dec': return dec(q());
      case 'int': { const x = q(); return isInt(x) ? (x.n < 0 ? '−' : '') + commas(String(Math.abs(x.n))) : dec(x); }
      case 'pct': return pct(q());
      default: return BANK().format(typeof v === 'object' ? toNumber(v) : v, fmt || 'text', unit);
    }
  }
  const ftext = v => format(v, 'frac'), dptext = v => format(v, 'decpct'), ntext = v => format(v, 'num');
  const OWN = { frac: 'ftext', decpct: 'dptext', num: 'ntext' };
  const rewrite = t => String(t).replace(/\{([^{}]+?)\|\s*(frac|decpct|num)\s*\}/g, (m, e, f) => `{chance.${OWN[f]}(${e})}`);
  function fill(t, params, unit) { return t == null ? '' : BANK().fill(rewrite(t), params || {}, unit); }
  const fillEvent = (name, params) => (name == null ? null : String(name).replace(/\{(\w+)\}/g, (m, k) => (params && k in params ? String(params[k]) : m)));
  const evalExpr = (expr, params) => BANK().eval(String(expr), params || {});

  /* ---------------- Fine Print ---------------- */
  const CHEATS = ['equiprobable', 'partToPart', 'gamblersFallacy', 'smallSample', 'complement', 'conversion'];
  /* the numbers in a claim's text: a/b, decimals, percents, whole numbers (commas allowed) */
  const NUM_RE = /(\d[\d,]*)\s*\/\s*(\d[\d,]*)|(\d[\d,]*(?:\.\d+)?|\.\d+)(\s*%)?/g;
  function numbersIn(t) {
    const out = []; let m; NUM_RE.lastIndex = 0;
    while ((m = NUM_RE.exec(t))) {
      if (m[1] != null) { const d = +m[2].replace(/,/g, ''); if (d) out.push(mk(+m[1].replace(/,/g, ''), d)); }
      else out.push(parseAnswer(m[3] + (m[4] ? '%' : '')));
    }
    return out;
  }
  function shown(t, name) {
    const s = String(t), ev = String(name || '');
    if (ev === 'total' || ev === 'size') { const ns = numbersIn(s); const v = ns.reduce((a, b) => add(a, b), ZERO); return { value: v, values: ns, chainOk: true }; }
    if (/^expect:/.test(ev) || /^count:/.test(ev)) { const ns = numbersIn(s); return { value: ns[ns.length - 1] || null, values: ns.slice(-1), chainOk: ns.length > 0 }; }
    // the chain after the last "P(…)"
    const at = s.lastIndexOf('P(');
    let rest = s;
    if (at >= 0) { let depth = 0, j = at + 1; for (; j < s.length; j++) { if (s[j] === '(') depth++; else if (s[j] === ')' && --depth === 0) break; } rest = s.slice(j + 1); }
    const ns = numbersIn(rest);
    return { value: ns[0] || null, values: ns, chainOk: ns.length > 0 && ns.every(v => eq(v, ns[0])) };
  }
  /* the digit slip: 3/4 read as 0.34 */
  function digitSlip(q) { q = F(q); return q.n >= 0 && q.d > 1 ? parseAnswer('0.' + q.n + q.d) : null; }
  function cheatValue(name, e, ev) {
    e = exp(e);
    if (name === 'equiprobable') return mk(1, e.labels.length);
    if (name === 'partToPart') { const f = count(e, ev); return f === e.n ? null : mk(f, e.n - f); }
    if (name === 'complement') return sub(ONE, P(e, ev));
    if (name === 'conversion') return digitSlip(P(e, ev));
    return null;
  }
  function isCheat(name, v, e, ev, params, raw) {
    if (v == null) return false;
    e = exp(e, params);
    const honest = value(e, ev);
    if (eq(v, honest)) return false;
    switch (name) {
      case 'equiprobable': case 'partToPart': case 'complement': { const c = cheatValue(name, e, ev); return !!c && eq(v, c); }
      case 'gamblersFallacy': return true;                                // after a streak: any value but the fair one
      case 'conversion': { const c = cheatValue(name, e, ev); return cmp(v, ONE) > 0 || cmp(v, ZERO) < 0 || (!!c && eq(v, c)); }
      case 'smallSample': {
        const m = /\{(\w+)\}\s*\/\s*\{(\w+)\}/.exec(String(raw || ''));
        if (!m || !params || !(m[1] in params) || !(m[2] in params)) return false;
        const t = +params[m[2]];
        return t > 0 && t <= 20 && eq(v, mk(+params[m[1]], t));
      }
      default: return false;
    }
  }
  const source = it => (it && it._src) || it;
  /* bank-instance params → the content's names (Chance.pool renames upper-case params: N → _N) */
  function unmangle(item, params) {
    const out = { ...(params || {}) };
    for (const k of Object.keys(out)) if (k[0] === '_' && item.params && item.params[k.slice(1)] !== undefined && !(k.slice(1) in out)) { out[k.slice(1)] = out[k]; delete out[k]; }
    return out;
  }
  function poster(item, params) {
    item = source(item); params = unmangle(item, params);
    const e = exp(item.exp, params);
    const claims = (item.claims || []).map((c, i) => {
      const t = fill(c.text, params), ev = fillEvent(c.event, params), sh = shown(t, ev), honest = value(e, ev);
      return { i, text: t, raw: c.text, event: ev, bad: !!c.bad, shown: sh.value, values: sh.values, chainOk: sh.chainOk,
        shownText: sh.value ? text(sh.value) : '', honest, honestText: isProbEvent(ev) ? text(honest) : format(honest, 'int'),
        ok: !!sh.value && sh.chainOk && eq(sh.value, honest), working: working(e, ev) };
    });
    const bad = claims.findIndex(c => c.bad);
    const fmt = item.format || 'frac', answer = exact(item.formula, params);
    const distractors = {};
    for (const [k, x] of Object.entries(item.distractors || {})) { const v = exact(x, params); distractors[k] = { value: v, text: format(v, fmt === 'int' ? 'frac' : fmt) }; }
    return { id: item.id, title: item.title, cheat: item.cheat || 'legit', level: item.level || 1, misconception: item.misconception || null,
      exp: e, params, claims, bad, q: fill(item.q, params), answer, answerText: format(answer, fmt === 'int' ? 'frac' : fmt), distractors,
      why: Object.fromEntries(Object.entries(item.why || {}).map(([k, w]) => [k, fill(w, params)])), explain: fill(item.explain, params) };
  }

  /* ---------------- the Lab ---------------- */
  function stepSeed(seed, id, i) { return hashSeed(`${seed == null ? 1 : seed}:${id || ''}:${i}`); }
  function steps(item, params, seed) {
    item = source(item);
    const Pm = unmangle(item, params);
    let e = null;
    const ev = item.event ? fillEvent(item.event, Pm) : null;
    return (item.steps || []).map((s, i) => {
      const o = { i, type: s.type, text: fill(s.text, Pm), hint: s.hint ? fill(s.hint, Pm) : '', format: s.format || null, last: i === item.steps.length - 1 };
      if (s.type === 'numpad') { o.value = exact(s.formula, Pm); o.answerText = format(o.value, s.format || 'frac'); }
      else if (s.type === 'choice') {
        o.options = (s.options || []).map(t => fill(t, Pm)); o.correct = s.correct; o.answerText = o.options[o.correct];
        if (s.truth) { const v = evalExpr(s.truth, Pm); if (typeof v !== 'boolean') throw new Error(`chance: ${item.id} step ${i + 1}: truth "${s.truth}" gives ${JSON.stringify(v)}`); o.truth = v; }
      } else if (s.type === 'run') {
        if (!e) e = exp(item.exp, Pm);
        const q = exact(String(s.trials), Pm);
        if (!isInt(q) || q.n < 1) throw new Error(`chance: ${item.id} step ${i + 1}: trials = ${text(q)}`);
        o.trials = q.n; o.seed = stepSeed(seed, item.id, i);
        o.sim = simulate(e, q.n, o.seed, ev);
        Pm.x = o.sim.x; Pm.N = q.n;
        o.x = o.sim.x; o.N = q.n; o.answerText = `${o.x} of ${o.N}`;
      } else throw new Error(`chance.steps: ${item.id} step ${i + 1}: unknown type "${s.type}"`);
      return o;
    });
  }
  function check(step, typed) {
    if (!step) return false;
    if (step.type === 'choice') return typed === step.correct;
    if (step.type === 'run') return true;
    if (step.type !== 'numpad') return false;
    const s = String(typed == null ? '' : typed).trim(), fmt = step.format || 'frac';
    if (fmt === 'frac' || fmt === 'decpct') { const q = parseAnswer(s); return !!q && eq(q, step.value); }
    if (fmt === 'int') { const q = parseAnswer(s); return !!q && !/[/%]/.test(s) && eq(q, step.value); }
    if (fmt === 'num' || fmt === 'dec') { if (/\//.test(s)) return false; const q = parseAnswer(s.replace(/%$/, '')); return !!q && eq(q, step.value); }
    if (fmt === 'pct') { const q = parseAnswer(/%$/.test(s) ? s : s + '%'); return !!q && eq(q, step.value); }
    return false;
  }
  const byLevel = list => list.map((x, i) => [x, i]).sort((a, b) => ((source(a[0].item || a[0]).level || 0) - (source(b[0].item || b[0]).level || 0)) || a[1] - b[1]).map(a => a[0]);

  /* ---------------- pools the shared core/bank.js accepts ----------------
     The content uses things core/bank.js doesn't know (docs/QUESTION_BANK.md §7): the "frac" format, {expr|frac} in text,
     Lab items whose answers live in `steps` (no top-level formula / explain), items without params, and an upper-case
     param (N). Chance.pool(json) returns a copy with, per item (listed in `_normalized`; `_src` = the content item):
       - format frac | int → "text", formula and distractors wrapped in chance.ftext(…) (exact reduced fractions,
         integers as "1"): the printed options are what the student sees;
       - {expr|frac} / {expr|decpct} / {expr|num} in every text → {chance.ftext(expr)} etc.;
       - an upper-case param renamed (N → _N) everywhere in the copy (Chance.steps / poster map it back);
       - a step item: params {} when missing, answer "numpad", formula = the last numpad step computable from the params
         alone (not x / N from a run), format int or dec1, explain = the last step's text. The Lab NEVER shows these:
         it uses Chance.steps(). */
  function pool(json) {
    const out = JSON.parse(JSON.stringify(json));
    for (const it of out.items || []) {
      const src = JSON.parse(JSON.stringify(it)), changed = [];
      // upper-case params → _Name (whole-word, in every string of the copy)
      const up = Object.keys(it.params || {}).filter(k => /^[A-Z]/.test(k));
      if (up.length) {
        const re = new RegExp(`\\b(${up.join('|')})\\b`, 'g');
        const walk = v => (typeof v === 'string' ? v.replace(re, '_$1') : Array.isArray(v) ? v.map(walk) : v && typeof v === 'object' ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, walk(x)])) : v);
        for (const k of Object.keys(it)) if (!['id', 'tags', 'misconception', 'title', 'params'].includes(k)) it[k] = walk(it[k]);
        it.params = Object.fromEntries(Object.entries(it.params).map(([k, v]) => [up.includes(k) ? '_' + k : k, walk(v)]));
        changed.push(`param(s) ${up.join(', ')} → ${up.map(k => '_' + k).join(', ')}`);
      }
      if (Array.isArray(it.steps) && it.formula === undefined) {
        if (!it.params) { it.params = {}; changed.push('params {}'); }
        const names = new Set(Object.keys(it.params));
        const computable = s => s.type === 'numpad' && (String(s.formula).match(/[A-Za-z_$][\w$]*/g) || []).every(n => names.has(n));
        const last = it.steps.filter(computable).pop();
        it.answer = 'numpad';
        it.formula = last ? last.formula : '0';
        it.format = last && last.format === 'int' ? 'int' : 'dec1';
        changed.push(`step item: formula = ${last ? 'the last computable numpad step' : '0 (no numeric step)'}, format ${it.format}`);
        if (it.explain === undefined) {                   // the last step text whose {…} use only the params (not x / N)
          const fills = t => (String(t).match(/\{([^{}]+)\}/g) || []).every(b => (b.match(/[A-Za-z_$][\w$]*/g) || []).every(n => names.has(n) || n === 'chance'));
          const st = it.steps.slice().reverse().find(s => s.text && fills(s.text));
          it.explain = st ? st.text : it.q; changed.push('explain = the last step text that fills from the params');
        }
      }
      if (it.format === 'frac' || (it.format === 'int' && it.distractors)) {
        const f0 = it.format;
        it.format = 'text';
        if (typeof it.formula === 'string') it.formula = `chance.ftext(${it.formula})`;
        if (it.distractors) for (const k of Object.keys(it.distractors)) it.distractors[k] = `chance.ftext(${it.distractors[k]})`;
        changed.push(`format ${f0} → text + chance.ftext(…)`);
      }
      let rw = false;
      const rwf = v => { const r = rewrite(v); if (r !== v) rw = true; return r; };
      for (const f of ['q', 'explain']) if (typeof it[f] === 'string') it[f] = rwf(it[f]);
      if (it.why) for (const k of Object.keys(it.why)) it.why[k] = rwf(it.why[k]);
      if (Array.isArray(it.claims)) it.claims.forEach(c => { c.text = rwf(c.text); });
      if (Array.isArray(it.steps)) it.steps.forEach(s => { if (s.text) s.text = rwf(s.text); if (s.hint) s.hint = rwf(s.hint); });
      if (rw) changed.push('{…|frac} in text → {chance.ftext(…)}');
      Object.defineProperty(it, '_src', { value: src, enumerable: false });
      if (changed.length) it._normalized = changed;
    }
    return out;
  }

  const Chance = {
    frac, rational, eq, add, sub, mul, div, cmp, toNumber, isInt, terminates, text, dec, pct, decpct, likelihood, parseAnswer,
    exp, coin, die, spinner, bag, tiles, cards, space, event, count, P, expected, value, working, isProbEvent,
    rng, hashSeed, simulate, VIDEO,
    CHEATS, cheatValue, isCheat, shown, digitSlip, poster,
    steps, check, byLevel,
    exact, format, ftext, dptext, ntext, fill, fillEvent, eval: evalExpr, pool, source, unmangle,
  };
  // the 800-spin run the s4 chart draws: the SAME seed, so its first 8 spins are Jay's 8 (2 socks: the chart starts at
  // 1/4 and settles near 1/8); the count is the model's (tools/chancecheck.js proves both)
  VIDEO.s4Big.seed = VIDEO.s4Seed;
  VIDEO.s4Big.socks = simulate(WHEEL, VIDEO.s4Big.n, VIDEO.s4Big.seed, 'label:FREE SOCKS').x;
  root.Chance = Chance;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('chance', Chance);
  if (typeof module !== 'undefined' && module.exports) module.exports = Chance;
})(typeof window !== 'undefined' ? window : globalThis);
